import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/password-input";
import { resetPassword } from "@/lib/auth-client";

type ResetPasswordSearchParams = {
  token?: string;
  error?: string;
};

export const Route = createFileRoute("/auth/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): ResetPasswordSearchParams => {
    return {
      token: search.token as string | undefined,
      error: search.error as string | undefined,
    };
  },
});

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password must be less than 128 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

function ResetPasswordPage() {
  const { token, error: urlError } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Handle URL error parameter (e.g., INVALID_TOKEN from better-auth)
  useEffect(() => {
    if (urlError === "INVALID_TOKEN") {
      setError(
        "This password reset link is invalid or has expired. Please request a new one.",
      );
    } else if (urlError) {
      setError(`An error occurred: ${urlError}`);
    }
  }, [urlError]);

  // Check for missing token
  useEffect(() => {
    if (!token && !urlError) {
      setError(
        "Invalid password reset link. Please check your email for the correct link.",
      );
    }
  }, [token, urlError]);

  async function onSubmit(data: ResetPasswordFormValues) {
    if (!token) {
      setError("Missing reset token");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await resetPassword({
        newPassword: data.password,
        token,
      });

      if (result.error) {
        const errorData = result.error as {
          message?: string;
          code?: string;
        };
        if (
          errorData.code === "INVALID_TOKEN" ||
          errorData.code === "TOKEN_EXPIRED"
        ) {
          setError(
            "This password reset link is invalid or has expired. Please request a new one.",
          );
        } else {
          setError(
            errorData.message ||
              "Failed to reset password. Please try again or request a new link.",
          );
        }
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);

      // Redirect to signin after a short delay
      setTimeout(() => {
        navigate({ to: "/auth/signin" });
      }, 2000);
    } catch (err) {
      setError(
        (err as Error)?.message ||
          "An unexpected error occurred. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-sm p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <h2 className="text-xl font-semibold">
              Password Reset Successful!
            </h2>
            <p className="text-muted-foreground text-sm">
              Your password has been updated. Redirecting to sign in...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Error state - no token or URL error
  if (!token || urlError) {
    return (
      <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <h2 className="text-xl font-semibold">Invalid Reset Link</h2>
            <p className="text-muted-foreground text-sm">
              {error ||
                "This password reset link appears to be invalid or has expired."}
            </p>
            <div className="mt-4 p-4 bg-muted rounded-lg text-sm text-left w-full">
              <p className="font-medium mb-2">What can you do?</p>
              <p className="text-muted-foreground">
                Return to the sign in page and request a new password reset
                link.
              </p>
            </div>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: "/auth/signin" })}
            >
              Go to Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Normal form state
  return (
    <div className="bg-topography flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-3xl">
        <div className="flex flex-col gap-10 py-12">
          <Card className="p-0 my-8">
            <CardContent className="grid p-0 md:grid-cols-2">
              <div className="relative min-h-[500px] flex flex-col justify-center">
                <Form {...form}>
                  <form
                    className="p-10 md:p-14"
                    onSubmit={form.handleSubmit(onSubmit)}
                    noValidate
                  >
                    <div className="flex flex-col gap-8">
                      <div className="flex flex-col items-center text-center">
                        <img
                          src="/cte-logo.png"
                          alt="CTE Logo"
                          className="mb-4 h-12 w-auto"
                        />
                        <h1 className="text-2xl font-bold">
                          Reset Your Password
                        </h1>
                        <p className="text-muted-foreground text-balance mt-2">
                          Enter a new password for your account.
                        </p>
                      </div>

                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="relative pb-5">
                            <FormLabel>New Password</FormLabel>
                            <FormControl>
                              <PasswordInput
                                {...field}
                                id="password"
                                name="password"
                                placeholder="Enter your new password"
                                required
                                autoComplete="new-password"
                              />
                            </FormControl>
                            <FormMessage className="absolute left-0 bottom-0" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem className="relative pb-5">
                            <FormLabel>Confirm Password</FormLabel>
                            <FormControl>
                              <PasswordInput
                                {...field}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Confirm your new password"
                                required
                                autoComplete="new-password"
                              />
                            </FormControl>
                            <FormMessage className="absolute left-0 bottom-0" />
                          </FormItem>
                        )}
                      />

                      <div className="relative">
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Resetting Password...
                            </>
                          ) : (
                            "Reset Password"
                          )}
                        </Button>

                        {error && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                              <div>
                                <p>{error}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        Remember your password?{" "}
                        <button
                          type="button"
                          onClick={() => navigate({ to: "/auth/signin" })}
                          className="text-primary hover:underline underline-offset-4"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </form>
                </Form>
              </div>
              <div className="bg-muted relative hidden md:block">
                <img
                  src="/login-bg.jpg"
                  alt="Substation at night"
                  className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
                  loading="lazy"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
